import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";
import { TYPE_BADGE_COLORS } from "../../utils/typeColors";

interface Props { payload: VisualPayload }

interface MoveSection {
  section: string;
  move_name: string;
  move_type: string;
  poke_type: string;
  power: number | null;
  energy_delta: number | null;
  turns: number | null;
  dpt: number | null;
  ept: number | null;
  dpe: number | null;
  dps: number | null;
  is_elite: boolean;
  tier?: string;
  tier_label?: string;
  tier_reason?: string;
}

function StatCell({ label, value, highlight }: { label: string; value: string | number | null; highlight?: boolean }) {
  return (
    <div className={`rounded-lg p-2 text-center ${highlight ? "bg-white/[0.08]" : "bg-white/[0.04]"}`}>
      <p className="text-[10px] text-white/30 uppercase tracking-wide mb-0.5">{label}</p>
      <p className={`text-sm font-semibold ${highlight ? "text-white" : "text-white/60"}`}>
        {value != null ? value : "—"}
      </p>
    </div>
  );
}

export default function MoveComparisonCard({ payload }: Props) {
  const moveSections = payload.sections.filter((s) => s.section === "move").map((s) => s as unknown as MoveSection);

  if (moveSections.length < 2) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
        <p className="text-sm text-white/40">Need at least two moves to compare.</p>
      </CardShell>
    );
  }

  const [a, b] = moveSections;

  // Determine which side is "better" for each stat (higher = better for power, dpt, ept, dpe, dps)
  const betterPower = (a.power ?? 0) >= (b.power ?? 0) ? "a" : "b";
  const betterDpt = (a.dpt ?? 0) >= (b.dpt ?? 0) ? "a" : "b";
  const betterEpt = (a.ept ?? 0) >= (b.ept ?? 0) ? "a" : "b";
  const betterDpe = (a.dpe ?? 0) >= (b.dpe ?? 0) ? "a" : "b";
  const betterDps = (a.dps ?? 0) >= (b.dps ?? 0) ? "a" : "b";

  const renderCol = (m: MoveSection, side: "a" | "b") => {
    const typeBadge = TYPE_BADGE_COLORS[m.poke_type?.toLowerCase()] ?? "bg-white/10 text-white/50";
    const isFast = m.move_type === "fast";
    const energyLabel = m.energy_delta != null
      ? (m.energy_delta > 0 ? `+${m.energy_delta}` : String(m.energy_delta))
      : null;

    return (
      <div className="flex-1 min-w-0">
        {/* Move header */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          <span className="text-sm font-semibold text-white truncate">{m.move_name}</span>
          {m.poke_type && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${typeBadge}`}>{m.poke_type}</span>
          )}
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/50 font-medium">
            {isFast ? "Fast" : "Charged"}
          </span>
          {m.is_elite && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400 font-medium">Elite</span>
          )}
          {m.tier && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/80 font-semibold" title={m.tier_reason}>{m.tier_label ?? m.tier}</span>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2">
          <StatCell label="Power" value={m.power} highlight={betterPower === side} />
          <StatCell label="Energy" value={energyLabel} />
          {m.turns != null && <StatCell label="Turns" value={m.turns} />}
          {m.dpt != null && <StatCell label="DPT" value={m.dpt.toFixed(2)} highlight={betterDpt === side} />}
          {isFast && m.ept != null && <StatCell label="EPT" value={m.ept.toFixed(2)} highlight={betterEpt === side} />}
          {!isFast && m.dpe != null && <StatCell label="DPE" value={m.dpe.toFixed(2)} highlight={betterDpe === side} />}
          {m.dps != null && <StatCell label="DPS (PvE)" value={m.dps.toFixed(2)} highlight={betterDps === side} />}
        </div>
      </div>
    );
  };

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      <div className="flex gap-4">
        {renderCol(a, "a")}
        <div className="w-px bg-white/10 self-stretch" />
        {renderCol(b, "b")}
      </div>
    </CardShell>
  );
}
